import { useEffect, useState } from "react";
import { Send, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";
import { api, errMsg, fmtDate } from "@/lib/api";
import { STATUSES, UPDATE_TEMPLATES } from "@/lib/constants";
import { Card, SectionTitle, EstimateView, useAct, ContactButtons } from "@/components/common";

const sel = "w-full h-9 rounded-md border border-slate-300 px-2 text-sm bg-white";

function StatusControl({ r, act, busy }) {
  const { t } = useI18n();
  const [s, setS] = useState(r.status);
  const [note, setNote] = useState("");
  return (
    <Card data-testid="owner-status-control">
      <div className="flex items-center justify-between mb-4">
        <SectionTitle>{t("d.set_status")}</SectionTitle>
        <label className="flex items-center gap-2 text-sm font-medium text-red-700">{t("d.urgent")}
          <Switch checked={!!r.urgent} onCheckedChange={(v) => act(`/requests/${r.id}/urgent`, { urgent: v })} data-testid="owner-urgent-switch" />
        </label>
      </div>
      <select value={s} onChange={(e) => setS(e.target.value)} className={sel} data-testid="owner-status-select">
        {STATUSES.map((x) => <option key={x} value={x}>{t(`st.${x}`)}</option>)}
      </select>
      <Input className="mt-2" placeholder={t("o.note_opt")} value={note} onChange={(e) => setNote(e.target.value)} data-testid="owner-status-note" />
      <Button className="w-full mt-2 rounded-full" disabled={busy} onClick={() => act(`/requests/${r.id}/status`, { status: s, note }, t("o.status_updated"))} data-testid="owner-status-save">{t("d.save")}</Button>
    </Card>
  );
}

function Assign({ r, act, busy, reload }) {
  const { t } = useI18n();
  const [list, setList] = useState([]);
  const [pick, setPick] = useState("");
  const [recs, setRecs] = useState(r.recommended || []);
  useEffect(() => { api.get(`/owner/requests/${r.id}/eligible`).then((x) => setList(x.data)); }, [r.id]);
  useEffect(() => { setRecs(r.recommended || []); }, [r.recommended]);
  const toggle = (id) => setRecs(recs.includes(id) ? recs.filter((x) => x !== id) : [...recs, id].slice(-2));
  const saveRecs = async () => {
    try { await api.put(`/owner/requests/${r.id}/recommend`, { contractor_ids: recs }); toast.success(t("o.recs_updated")); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const ok = list.filter((c) => c.service_ok);
  return (
    <Card data-testid="owner-assign-panel">
      <SectionTitle>{t("o.pros")}</SectionTitle>
      <p className="text-sm text-slate-600">{t("o.assigned")}: <b data-testid="owner-assigned-name">{r.contractor?.name || "—"}</b>{r.assignment && <span className="text-xs text-slate-400"> ({t(`o.as_${r.assignment.status}`)}, {t("o.by")} {t(`o.by_${r.assignment.by}`)})</span>}</p>
      <div className="mt-3 flex gap-2">
        <select value={pick} onChange={(e) => setPick(e.target.value)} className={sel} data-testid="owner-assign-select">
          <option value="">{t("o.choose_pro")}</option>
          {ok.map((c) => <option key={c.id} value={c.id}>{c.name}{c.area_ok ? "" : ` (${t("o.outside")})`}</option>)}
        </select>
        <Button disabled={!pick || busy} onClick={() => act(`/requests/${r.id}/assign`, { contractor_id: pick }, t("o.assigned_ok"))} className="bg-teal-600 hover:bg-teal-700" data-testid="owner-assign-btn">{t("o.assign")}</Button>
      </div>
      <p className="mt-5 text-xs font-semibold uppercase tracking-wider text-slate-500">{t("o.recs_max")}</p>
      <div className="mt-2 space-y-1.5 max-h-48 overflow-y-auto">
        {ok.map((c) => (
          <label key={c.id} className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={recs.includes(c.id)} onChange={() => toggle(c.id)} data-testid={`owner-rec-${c.id}`} />
            {c.name} <span className="text-xs text-slate-400">{c.area_ok ? t("o.in_area") : t("o.outside")} · {c.jobs_completed} {t("o.jobs")}</span>
          </label>
        ))}
        {ok.length === 0 && <p className="text-sm text-slate-400">{t("o.no_pros")}</p>}
      </div>
      <div className="mt-3 flex gap-2">
        <Button size="sm" variant="outline" onClick={saveRecs} data-testid="owner-save-recs-btn">{t("o.save_recs")}</Button>
        <Button size="sm" variant="ghost" onClick={() => act(`/owner/requests/${r.id}/recommend`, {}, t("o.automatch_ok"))} data-testid="owner-rematch-btn"><RefreshCw className="h-3.5 w-3.5 me-1" />{t("o.automatch")}</Button>
      </div>
      {r.decline_log?.length > 0 && <div className="mt-4 text-xs text-slate-500">{r.decline_log.map((d, i) => <p key={i}>{t("o.declined")}: {d.reason} · {fmtDate(d.at)}</p>)}</div>}
    </Card>
  );
}

function Updates({ r, act, busy }) {
  const { t } = useI18n();
  return (
    <Card data-testid="owner-updates-panel">
      <SectionTitle>{t("o.send_update")}</SectionTitle>
      <div className="space-y-2">
        {UPDATE_TEMPLATES.map((u, i) => (
          <button key={i} disabled={busy} onClick={() => act(`/requests/${r.id}/updates`, { title: t(`o.tpl${i}_t`), body: t(`o.tpl${i}_b`), set_status: u.status && (!u.from || u.from.includes(r.status)) ? u.status : null }, t("o.update_sent"))}
            className="w-full flex items-center justify-between gap-2 text-start rounded-xl border border-slate-200 px-3 py-2 text-sm hover:border-teal-500 hover:bg-teal-50 transition-colors" data-testid={`owner-update-template-${i}`}>
            {t(`o.tpl${i}_t`)}<Send className="h-3.5 w-3.5 text-teal-600 shrink-0" />
          </button>
        ))}
      </div>
      {r.customer_updates?.length > 0 && (
        <div className="mt-4 border-t pt-3 space-y-1" data-testid="owner-updates-log">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("o.sent_log")}</p>
          {[...r.customer_updates].reverse().map((u) => <p key={u.id} className="text-xs text-slate-600">{u.title} · <span className="font-mono">{fmtDate(u.at)}</span> · {u.sent_by}</p>)}
        </div>
      )}
    </Card>
  );
}

function Notes({ r, act, busy }) {
  const { t } = useI18n();
  const [body, setBody] = useState("");
  const [kind, setKind] = useState("note");
  const add = async () => { if (!body.trim()) return; await act(`/requests/${r.id}/notes`, { body, kind }); setBody(""); };
  return (
    <Card data-testid="owner-notes-panel">
      <SectionTitle>{t("o.notes_t")}</SectionTitle>
      <div className="space-y-2 max-h-60 overflow-y-auto">
        {(r.internal_notes || []).map((n) => (
          <div key={n.id} className="rounded-xl bg-amber-50 px-3 py-2 text-sm" data-testid="owner-note-item">
            <span className="text-[10px] uppercase font-semibold text-amber-700">{n.kind === "contact" ? t("o.cust_contact") : t("o.note")}</span>
            <p>{n.body}</p><p className="text-[10px] text-slate-400 font-mono">{fmtDate(n.at)} · {n.author}</p>
          </div>
        ))}
      </div>
      <div className="mt-3 flex gap-2">
        <select value={kind} onChange={(e) => setKind(e.target.value)} className="h-9 rounded-md border border-slate-300 px-2 text-sm" data-testid="owner-note-kind"><option value="note">{t("o.note")}</option><option value="contact">{t("o.contact")}</option></select>
        <Input value={body} onChange={(e) => setBody(e.target.value)} placeholder={t("o.add_note")} data-testid="owner-note-input" />
        <Button disabled={busy} onClick={add} data-testid="owner-note-add-btn">{t("o.add")}</Button>
      </div>
    </Card>
  );
}

export default function OwnerPanel({ r, reload }) {
  const { t } = useI18n();
  const [busy, act] = useAct(reload);
  return (
    <div className="space-y-6">
      <StatusControl key={r.status} r={r} act={act} busy={busy} />
      <Assign r={r} act={act} busy={busy} reload={reload} />
      {r.estimate && (
        <Card data-testid="owner-estimate-panel">
          <SectionTitle>{t("d.estimate")}</SectionTitle>
          <EstimateView est={r.estimate} />
          {r.estimate.status === "pending_review" && <Button className="w-full mt-4 rounded-full bg-teal-600 hover:bg-teal-700" disabled={busy} onClick={() => act(`/requests/${r.id}/estimate/send`, {}, t("o.est_sent"))} data-testid="owner-send-estimate-btn">{t("o.send_est")}</Button>}
        </Card>
      )}
      <Card data-testid="owner-contact-card">
        <SectionTitle>{t("d.contact")}</SectionTitle>
        <p className="text-sm mb-3" data-testid="owner-customer-contact">{r.contact_name} · {r.contact_phone} · {t(`m.${r.contact_method}`)}</p>
        <ContactButtons phone={r.contact_phone} size="md" text={t("o.wa_text", { name: r.contact_name, n: r.number })} />
        <Button className="w-full mt-4 rounded-full bg-teal-600 hover:bg-teal-700" disabled={busy || r.customer_updates?.some((x) => ["Your request is confirmed", "Tu solicitud está confirmada"].includes(x.title))} data-testid="owner-confirm-client-btn"
          onClick={() => act(`/requests/${r.id}/updates`, { title: t("o.confirm_title"), body: t("o.confirm_body", { n: r.number }), set_status: r.status === "new" ? "needs_review" : null }, t("o.confirm_sent"))}>
          {t("o.confirm_btn")}
        </Button>
        <p className="text-xs text-slate-500 mt-2">{t("o.confirm_hint")}</p>
      </Card>
      <Updates r={r} act={act} busy={busy} />
      <Notes r={r} act={act} busy={busy} />
    </div>
  );
}
